import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { RouterModule } from '@angular/router';
import {
  IActivityEvent,
  IActivityEventTarget,
  IActivityFilters,
} from '../../../_interfaces/IActivityEvent';
import { ActivityEventsService } from '../../../_services/activity-events/activity-events.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';

@Component({
  standalone: true,
  selector: 'app-roles-table-logs',
  templateUrl: './table-logs.component.html',
  styleUrls: ['./table-logs.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    TableModule,
    TranslatePipe,
  ],
})
export class TableLogsComponent extends BaseComponent implements OnInit {
  public events: IActivityEvent[] = [];
  public moment = moment;
  public totalRecords: number = 0;
  public loading: boolean = false;
  public rowsPerPage: number = 20;
  public expandedRows = new Set<number>();

  public filters = {
    q: '',
    action: '',
    users_id: null as number | null,
    event_code: '',
    resource_type: '',
    site_id: null as number | null,
    from: '',
    to: '',
  };

  public meta = {
    actions: [] as string[],
    event_codes: [] as string[],
    resource_types: [] as string[],
    users: [] as Array<{ id: number; email?: string; name?: string }>,
    sites: [] as Array<{ id: number; name: string }>,
    retention: {
      active_total: 0,
      archived_total: 0,
      last_archive_at: null as string | null,
    },
  };

  private lazyState: { first: number; rows: number } = {
    first: 0,
    rows: this.rowsPerPage,
  };

  constructor(
    public activityEventsService: ActivityEventsService,
    public errorsService: ErrorsService,
    public messageService: MessageService,
    public translate: TranslateService,
    public session: SessionService,
  ) {
    super(session);
  }

  private buildFilters(): IActivityFilters {
    const rows = this.lazyState.rows || this.rowsPerPage;
    const page = Math.floor((this.lazyState.first || 0) / rows);
    const from = this.filters.from
      ? new Date(`${this.filters.from}T00:00:00`).toISOString()
      : undefined;
    const to = this.filters.to
      ? new Date(`${this.filters.to}T23:59:59`).toISOString()
      : undefined;

    return {
      page,
      size: rows,
      from,
      to,
      q: this.filters.q?.trim() || undefined,
      actions: this.filters.action ? [this.filters.action] : undefined,
      users_ids: this.filters.users_id ? [this.filters.users_id] : undefined,
      event_codes: this.filters.event_code ? [this.filters.event_code] : undefined,
      resource_type: this.filters.resource_type || undefined,
      site_ids: this.filters.site_id ? [this.filters.site_id] : undefined,
    };
  }

  private loadMeta(): void {
    this.activityEventsService.getFiltersMeta(this.customers_id).subscribe({
      next: (meta) => {
        const eventCodes = [...(meta.event_codes || [])].sort((a, b) =>
          this.eventCodeLabel(a).localeCompare(this.eventCodeLabel(b)),
        );
        this.meta = {
          ...this.meta,
          ...meta,
          event_codes: eventCodes,
        };
      },
    });
  }

  loadLogs(event: any = this.lazyState): void {
    this.loading = true;
    this.lazyState = {
      first: Number(event?.first || 0),
      rows: Number(event?.rows || this.rowsPerPage),
    };

    this.activityEventsService
      .getAll(this.customers_id, this.buildFilters())
      .subscribe({
      next: (response) => {
        this.events = response.data;
        this.totalRecords = response.total;
        this.loading = false;
      },
      error: (error) => {
        this.events = [];
        this.totalRecords = 0;
        this.loading = false;
        const code = this.errorsService.getMessageFromRegistryError(error);
        this.messageService.add({
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.translate.instant(`MESSAGES.MARKETING.CHANNELS.ERROR.${code}`),
          severity: 'error',
        });
      },
    });
  }

  applyFilters(): void {
    this.lazyState.first = 0;
    this.loadLogs(this.lazyState);
  }

  clearFilters(): void {
    this.filters = {
      q: '',
      action: '',
      users_id: null,
      event_code: '',
      resource_type: '',
      site_id: null,
      from: '',
      to: '',
    };
    this.applyFilters();
  }

  toggleExpanded(eventId: number): void {
    if (this.expandedRows.has(eventId)) {
      this.expandedRows.delete(eventId);
      return;
    }
    this.expandedRows.add(eventId);
  }

  isExpanded(eventId: number): boolean {
    return this.expandedRows.has(eventId);
  }

  activityActionLabel(action: string): string {
    const key = `ACTIVITY.ACTION.${action}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : action;
  }

  resourceTypeLabel(resourceType: string): string {
    const normalized = this.normalizeResourceTypeForUi(resourceType);
    const key = `ACTIVITY.RESOURCE.${normalized}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : normalized;
  }

  eventTitle(event: IActivityEvent): string {
    const key = `ACTIVITY.EVENT.${event.event_code}.TITLE`;
    const translated = this.translate.instant(key, event.summary_params || {});
    if (translated !== key) return translated;

    return this.translate.instant('ACTIVITY.EVENT.FALLBACK.TITLE', {
      action: this.activityActionLabel(event.action),
      resource: this.resourceTypeLabel(event.primary_resource.type),
    });
  }

  eventDescription(event: IActivityEvent): string {
    const key = `ACTIVITY.EVENT.${event.event_code}.DESCRIPTION`;
    const translated = this.translate.instant(key, event.summary_params || {});
    if (translated !== key) return translated;

    return this.translate.instant('ACTIVITY.EVENT.FALLBACK.DESCRIPTION', {
      code: event.event_code,
    });
  }

  fieldLabel(event: IActivityEvent, field: string): string {
    const resourceType = this.normalizeResourceTypeForUi(event.primary_resource.type);
    const specificKey = `ACTIVITY.FIELD.${resourceType}.${field}`;
    const translatedSpecific = this.translate.instant(specificKey);
    if (translatedSpecific !== specificKey) return translatedSpecific;

    const commonKey = `ACTIVITY.FIELD.COMMON.${field}`;
    const translatedCommon = this.translate.instant(commonKey);
    return translatedCommon !== commonKey ? translatedCommon : field;
  }

  private normalizeCodeLabel(code: string): string {
    return (code || '')
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private normalizeResourceTypeForUi(resourceType: string): string {
    const normalized = String(resourceType || '')
      .trim()
      .toUpperCase();
    if (normalized.endsWith('IES')) {
      return `${normalized.slice(0, -3)}Y`;
    }
    if (normalized.endsWith('S')) {
      return normalized.slice(0, -1);
    }
    return normalized;
  }

  eventCodeLabel(eventCode: string): string {
    const key = `ACTIVITY.EVENT.${eventCode}.TITLE`;
    const translated = this.translate.instant(key);
    if (translated !== key) return translated;
    return this.normalizeCodeLabel(eventCode);
  }

  resourceTargets(event: IActivityEvent): IActivityEventTarget[] {
    const map = new Map<string, IActivityEventTarget>();
    const primaryId = event.primary_resource?.id;
    if (primaryId) {
      const primary: IActivityEventTarget = {
        type: event.primary_resource.type,
        id: primaryId,
        label: event.primary_resource.label,
        link_kind: 'PRIMARY',
      };
      map.set(`${primary.type}:${primary.id}:PRIMARY`, primary);
    }

    for (const target of event.targets || []) {
      map.set(`${target.type}:${target.id}:${target.link_kind}`, target);
    }
    return Array.from(map.values());
  }

  targetRouterLink(target: IActivityEventTarget): any[] | null {
    const normalizedType = this.normalizeResourceTypeForUi(target.type);
    if (normalizedType === 'PATIENT') {
      return [
        '/administration',
        this.customer_route_param,
        'patients',
        Number(target.id),
        'profile',
      ];
    }

    if (normalizedType === 'APPOINTMENT') {
      const customer = this.session.getCustomer();
      const site = this.session.getSite();
      if (!customer || !site) return null;
      return ['/', customer.identifier, site.identifier, 'appointments'];
    }

    return null;
  }

  targetQueryParams(target: IActivityEventTarget): Record<string, any> | null {
    if (this.normalizeResourceTypeForUi(target.type) === 'APPOINTMENT') {
      return { appointmentId: target.id };
    }
    return null;
  }

  getChanges(event: IActivityEvent): Array<{ field: string; before: any; after: any; masked: boolean }> {
    return (event?.changes || []).filter((change) => {
      const before = this.normalizeChangeValue(change?.before);
      const after = this.normalizeChangeValue(change?.after);
      if (!before && !after) return false;
      return before !== after;
    });
  }

  hasExpandedInfo(event: IActivityEvent): boolean {
    return this.getChanges(event).length > 0;
  }

  formatChangeValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  hasBeforeValue(change: { before: any }): boolean {
    const value = this.normalizeChangeValue(change?.before);
    return !!value;
  }

  private normalizeChangeValue(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadMeta();
  }
}
